import * as React from "react";
import { ConsentForm, ConsentType } from "intake_shared/src/types";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { v4 as uuidv4 } from "uuid";
import {
    addNewConsentForm,
    updateConsentCache,
} from "../../../../../store/consent_form";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Input } from "../../../bits/Input/Input";
import { Button } from "../../../bits/Button/Button";
import styles from "./AddConsentFormDialog.module.scss";
import { cn } from "../../../../helpers";
import TrashIcon from "../../../icons/Trash";

interface ConsentFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: (form: ConsentForm) => void;
}

export default function AddConsentFormDialog({
    open,
    onOpenChange,
    onSave,
}: ConsentFormDialogProps) {
    const olc = React.useContext(CacheContext);
    const [formName, setFormName] = React.useState("");
    const [types, setTypes] = React.useState<ConsentType[]>([
        { id: uuidv4(), name: "", link: "" },
    ]);

    const handleAddType = () => {
        setTypes([...types, { id: uuidv4(), name: "", link: "" }]);
    };

    const handleRemoveType = (id: string) => {
        if (types.length > 1) {
            setTypes(types.filter((type) => type.id !== id));
        }
    };

    const handleTypeChange = (
        id: string,
        field: keyof ConsentType,
        value: string
    ) => {
        setTypes(
            types.map((type) =>
                type.id === id ? { ...type, [field]: value } : type
            )
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newForm: ConsentForm = {
            id: uuidv4(),
            name: formName,
            types: types.filter(
                (type) => type.name.trim() !== "" || type.link.trim() !== ""
            ),
        };
        addNewConsentForm(newForm);
        const returnedConsentForms = await updateConsentCache();
        if (returnedConsentForms) {
            olc?.updateConsentForms(returnedConsentForms);
        }
        onSave(newForm);
        onOpenChange(false);
        resetForm();
    };

    const resetForm = () => {
        setFormName("");
        setTypes([{ id: uuidv4(), name: "", link: "" }]);
    };

    const isValid = formName.trim() !== "";

    return (
        <Dialog
            title="Add New Consent Form"
            open={open}
            onClose={() => onOpenChange(false)}
            saveText="Save Consent Form"
            submitBtnFormValue="add-consent-form"
            onCancel={() => {
                resetForm();
                onOpenChange(false);
            }}
            isSubmitBtnDisabled={!isValid}
        >
            <form id="add-consent-form" className="v2-form" onSubmit={handleSubmit}>
                <Input
                    label="Consent Form Name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    isRequired
                />
                <div className={styles.consentTypesWrapper}>
                    <div className={styles.consentTypesHeader}>
                        <p>Consent Types and Links</p>
                        <Button variant="secondary" onClick={handleAddType}>
                            Add Type
                        </Button>
                    </div>
                    {types.map((type) => (
                        <div
                            key={type.id}
                            className={cn(styles.consentType, "v2-form")}
                        >
                            <Input
                                id={`type-${type.id}`}
                                label="Type Name"
                                value={type.name}
                                placeholder="e.g., Adults or leave empty"
                                onChange={(e) =>
                                    handleTypeChange(type.id, "name", e.target.value)
                                }
                            />
                            <Input
                                id={`link-${type.id}`}
                                isRequired
                                label="Link"
                                value={type.link}
                                onChange={(e) =>
                                    handleTypeChange(type.id, "link", e.target.value)
                                }
                            />
                            <div className={styles.removeTypeButtonWrapper}>
                                <Button
                                    variant="normal"
                                    onClick={() => handleRemoveType(type.id)}
                                    className={styles.removeTypeButton}
                                >
                                    <TrashIcon />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </form>
        </Dialog>
    );
}
